import { useState } from 'react';
import { benefitsApi } from '../../../api/benefits.js';

const cents = (v) => (v == null ? '—' : `IDR ${Number(v).toLocaleString('en-US')}`);
const COVERAGE = ['employee', 'employee_spouse', 'family'];

export default function EnrollmentWizard() {
  const [step, setStep] = useState('select'); // select | preview | confirm | done
  const [employeeId, setEmployeeId] = useState('');
  const [eligiblePlans, setEligiblePlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [coverageLevel, setCoverageLevel] = useState('employee');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [elections, setElections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  async function loadEligible() {
    if (!employeeId) return;
    setLoading(true);
    setError(null);
    try {
      // Load eligible plans AND active elections together so the list renders
      // ONCE with the correct "already enrolled" state. Loading them in two
      // sequential awaits caused a double render: first every plan showed a
      // Select button, then a re-render removed buttons for enrolled plans —
      // a click landing during that churn could hit a button React unmounts in
      // the same frame, so its onClick never fired and the preview never opened.
      const [data, elData] = await Promise.all([
        benefitsApi.eligiblePlans({ employee_id: employeeId }),
        benefitsApi.listElections({ employee_id: employeeId, status: 'active' }),
      ]);
      setElections(elData?.items || []);
      setEligiblePlans(data?.items || []);
      setStep('select');
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load eligible plans');
    } finally {
      setLoading(false);
    }
  }

  function choosePlan(plan) {
    setSelectedPlan(plan);
    setCoverageLevel('employee');
    setStep('preview');
  }

  function previewCost() {
    if (!selectedPlan) return 0;
    const levels = selectedPlan.coverage_levels || {};
    return levels[coverageLevel] ?? selectedPlan.employee_cost_cents ?? 0;
  }

  async function confirm() {
    setLoading(true);
    setError(null);
    try {
      const election = await benefitsApi.createElection({
        employee_id: employeeId,
        plan_id: selectedPlan.id,
        coverage_level: coverageLevel,
        effective_date: effectiveDate || new Date().toISOString().slice(0, 10),
      });
      setResult(election?.election || election);
      setStep('done');
    } catch (e) {
      setError(e.response?.data?.message || 'Enrollment failed');
    } finally {
      setLoading(false);
    }
  }

  const alreadyEnrolled = (planId) => elections.some((e) => e.plan_id === planId);

  return (
    <section data-testid="benefits-wizard-page" data-loading={loading ? 'true' : 'false'}>
      <header className="page-header">
        <h1>Employee Enrollment Wizard</h1>
      </header>

      {error && <div className="alert error" data-testid="benefits-wizard-error">{error}</div>}

      {/* Step 0: enter employee ID */}
      <div className="card form-card">
        <label>Employee ID
          <input
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            data-testid="benefits-wizard-empid-input"
            placeholder="uuid"
          />
        </label>
        <button className="btn primary" onClick={loadEligible} data-testid="benefits-wizard-load" disabled={!employeeId || loading}>
          Load Eligible Plans
        </button>
      </div>

      {/* Step 1: plan selection */}
      {step !== 'done' && eligiblePlans.length > 0 && (
        <div className="card" data-testid="benefits-plan-catalog">
          <h3>Eligible Plans</h3>
          {eligiblePlans.map((plan) => (
            <div key={plan.id} className="benefit-plan-card" data-testid={`benefits-plan-card-${plan.id}`}>
              <div>
                <strong>{plan.name}</strong>
                <span className={`chip type-${plan.type}`}>{plan.type}</span>
                {plan.carrier && <span className="muted"> · {plan.carrier}</span>}
              </div>
              <div>Employee cost: {cents(plan.employee_cost_cents)}/pay period</div>
              {alreadyEnrolled(plan.id) ? (
                <span className="chip status-active">Already enrolled</span>
              ) : (
                <button
                  className="btn primary small"
                  data-testid={`benefits-coverage-select`}
                  onClick={() => choosePlan(plan)}
                >
                  Select
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Step 2: coverage preview */}
      {step === 'preview' && selectedPlan && (
        <div className="card form-card" data-testid="benefits-preview-card">
          <h3>Coverage Preview — {selectedPlan.name}</h3>
          <label>Coverage Level
            <select
              value={coverageLevel}
              onChange={(e) => setCoverageLevel(e.target.value)}
              data-testid="benefits-coverage-level-select"
            >
              {COVERAGE.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>Effective Date
            <input
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              data-testid="benefits-effective-date-input"
            />
          </label>
          <p>
            <strong>Your cost per pay period: {cents(previewCost())}</strong>
          </p>
          <div className="actions">
            <button className="btn ghost" onClick={() => setStep('select')}>Back</button>
            <button className="btn primary" onClick={confirm} data-testid="benefits-enroll-confirm" disabled={loading}>
              Confirm Enrollment
            </button>
          </div>
        </div>
      )}

      {/* Step 3: done */}
      {step === 'done' && result && (
        <div className="card" data-testid="benefits-enrollment-success">
          <h3>Enrollment Confirmed</h3>
          <p>Election ID: <code>{result.id}</code></p>
          <p>Status: <span className="chip status-active">{result.status}</span></p>
          <p>Effective: {result.effective_date}</p>
          <p>Per-pay cost: {cents(result.per_pay_cost_cents)}</p>
          <button className="btn" onClick={() => { setStep('select'); setSelectedPlan(null); setResult(null); }}>
            Enroll in Another Plan
          </button>
        </div>
      )}
    </section>
  );
}
